import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import debounce from "lodash.debounce";
import { message } from "antd";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Card,
  Typography,
  Button,
  MenuItem,
  Select,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Divider,
  Tooltip,
  Chip,
  Stack,
  DialogActions,
  TextField,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import BedIcon from "@mui/icons-material/Bed";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import SendIcon from "@mui/icons-material/Send";
import useAxiosInstance from "../helpers/useAxiosInstance";

const GuestCard = ({ open, setOpen, clientId }) => {
  const cancelTokenRef = useRef();
  const inputRef = useRef();
  const axiosInstance = useAxiosInstance()


  const [messageApi, contextHolder] = message.useMessage();

  const [selectedProperties, setSelectedProperties] = useState([]);
  const [selectedValue, setSelectedValue] = useState("");
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [clientData, setClientData] = useState({});
  const [emailSending, setEmailSending] = useState(false);

  const handleClose = () => {
    setOpen(false);
    setPropertyOptions([]);
    setSelectedValue("");
    setSelectedProperties([]);
    setClientData({});
    setSearchTerm("");
  };

  const handleRemoveProperty = (id) => {
    setSelectedProperties(selectedProperties.filter((p) => p?.id !== id));
  };

  const handleSelect = (value) => {
    if (!value) return;

    setSelectedValue(null);

    if (!selectedProperties?.some((p) => p?.id === value?.id)) {
      const defaultEmail =
        value?.email_1 ||
        value?.email_2 ||
        value?.email_3 ||
        value?.email_4 ||
        "";
      setSelectedProperties([
        ...selectedProperties,
        { ...value, selectedEmail: defaultEmail },
      ]);
    }

    setSearchTerm("");
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const fetchProperties = async (query) => {
    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancel("Operation canceled due to new request");
    }
    cancelTokenRef.current = axios.CancelToken.source();

    try {
      setLoading(true);
      const result = await axiosInstance.get(
        `${process.env.REACT_APP_API_URL}/guest-card/properties-list?query=${query}`,
        {
          cancelToken: cancelTokenRef.current.token,
        }
      );
      if (result?.data && result?.data?.data?.length) {
        setPropertyOptions(result.data?.data);
      } else {
        setPropertyOptions(result.data?.data || []);
      }
    } catch (error) {
      if (!axios.isCancel(error)) {
        setPropertyOptions([]);
        console.error("Error fetching properties:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const getClientDetails = async () => {
    try {
      const result = await axiosInstance.get(
        `${process.env.REACT_APP_API_URL}/guest-card/client-data/${clientId}`
      );
      if (result && result?.data?.data) {
        setClientData(result?.data?.data || {});
      } else {
        setClientData({});
      }
    } catch (error) {
      setClientData({});
      console.error(error);
    }
  };

  const debouncedFetch = useRef(
    debounce((query) => {
      if (query?.trim()?.length > 1) {
        fetchProperties(query);
      } else {
        setPropertyOptions([]);
      }
    }, 300)
  ).current;

  const handleSend = async () => {
    try {
      setEmailSending(true);
      const payload = {
        client: clientData,
        properties: selectedProperties,
        mode: "individual",
      };

      const result = await axiosInstance.post(
        `${process.env.REACT_APP_API_URL}/guest-card/send-mail`,
        payload
      );

      if (
        result?.data?.data &&
        Number(result?.data?.data?.accepted?.length) ===
          Number(selectedProperties?.length)
      ) {
        messageApi.open({
          type: "success",
          content: "Guest Card mails sent successfully!",
        });
        handleClose();
      } else {
        messageApi.open({
          type: "error",
          content: "Operation Failed!",
        });
      }

      console.log("Mail send result:", result?.data);
    } catch (error) {
      console.error("Error sending email:", error);
      messageApi.open({
        type: "error",
        content: "Operation Failed!",
      });
    } finally {
      setEmailSending(false);
    }
  };

  useEffect(() => {
    if (open && clientId) {
      getClientDetails();
    }
  }, [clientId, open]);

  useEffect(() => {
    debouncedFetch(searchTerm);
  }, [searchTerm]);

  return (
    <>
      {contextHolder}
      <Dialog
        open={open}
        onClose={() => {
          if (loading) return;
          handleClose();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 3,
            pt: 2,
            pb: 1,
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Send Guest Card
          </Typography>
          <IconButton
            onClick={() => {
              if (loading) return;
              handleClose();
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ px: 3, pt: 2, pb: 3, minHeight: 400 }}>
          <Card
            variant="outlined"
            sx={{
              mb: 3,
              borderRadius: 3,
              boxShadow: 2,
              p: 2,
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Client Info
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={1.2} direction="row" flexWrap="wrap" useFlexGap>
              <Chip
                icon={<PersonIcon />}
                label={`${clientData?.firstname} ${clientData?.lastname}`}
                color="primary"
                variant="filled"
              />
              <Chip
                icon={<CalendarTodayIcon />}
                label={`Move-in: ${new Date(clientData?.move_in_date)
                  .toLocaleDateString("en-US", {
                    month: "2-digit",
                    day: "2-digit",
                  })
                  .replace("/", "-")
                  .replace("/", "")} `}
                color="success"
              />

              <Chip
                icon={<BedIcon />}
                label={
                  Array.isArray(clientData?.no_of_bedrooms)
                    ? clientData?.no_of_bedrooms.join(", ")
                    : clientData?.no_of_bedrooms
                }
                color="info"
              />

              <Chip
                icon={<AttachMoneyIcon />}
                label={clientData?.budget}
                color="warning"
              />
            </Stack>
          </Card>

          <div className="my-2">
            <Autocomplete
              options={propertyOptions}
              autoFocus
              getOptionLabel={(option) => (option?.name ? option.name : "")}
              value={selectedValue || null}
              loading={loading}
              inputValue={searchTerm}
              loadingText="Loading..."
              onChange={(_, newValue) => {
                handleSelect(newValue);
              }}
              onInputChange={(_, newInputValue) => setSearchTerm(newInputValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="property-search"
                  className="w-full"
                  inputRef={inputRef}
                  label="Search Property"
                  variant="outlined"
                />
              )}
            />
          </div>

          <Box sx={{ mb: 3, mt: 3 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Selected Properties
            </Typography>
            <TableContainer
              component={Paper}
              sx={{
                borderRadius: 2,
                boxShadow: 1,
                maxHeight: 200,
                overflowY: "auto",
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#bbdefb" }}>
                    <TableCell>
                      <strong>Name</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Email</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Remove</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedProperties?.length > 0 ? (
                    selectedProperties?.map((property, idx) => {
                      const emailOptions = [
                        property?.email_1,
                        property?.email_2,
                        property?.email_3,
                        property?.email_4,
                      ].filter((email) => email?.trim());

                      return (
                        <TableRow key={idx} hover>
                          <TableCell>{property?.name || ""}</TableCell>
                          <TableCell>
                            <FormControl fullWidth size="small">
                              <Select
                                value={property?.selectedEmail || ""}
                                onChange={(e) => {
                                  const updated = [...selectedProperties];
                                  updated[idx].selectedEmail =
                                    e?.target?.value || "";
                                  setSelectedProperties(updated);
                                }}
                              >
                                {emailOptions?.map((email, i) => (
                                  <MenuItem key={i} value={email}>
                                    {email}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Remove">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() =>
                                  handleRemoveProperty(property?.id || "")
                                }
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No properties selected
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => {
              if (loading) return;
              handleClose();
            }}
            sx={{ borderRadius: 3, textTransform: "none", fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!selectedProperties?.length || emailSending}
            onClick={handleSend}
            sx={{
              borderRadius: 3,
              textTransform: "none",
              fontWeight: 600,
              minWidth: 100,
            }}
            startIcon={!emailSending && <SendIcon />}
          >
            {emailSending ? (
              <CircularProgress color="inherit" size={20} />
            ) : (
              "Send"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GuestCard;
